import { memo, useCallback, useEffect, useMemo, useState } from "react";

import { Activity } from "@hl/base-components/lib/assets/icons.generated";
import {
  BACKGROUND_COLOR,
  TEXT_COLOR,
} from "@hl/base-components/lib/theme/colors";
import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import {
  FEATURE_FLAGS,
  useFeatureFlags,
} from "@hl/shared-features/lib/features/auth/hooks";
import useLiveEvents from "@hl/shared-features/lib/features/live/useLiveEvents";
import {
  Box,
  Group,
  ScrollArea,
  Text,
  Transition,
  useMantineTheme,
  Stack,
} from "@mantine/core";

import { MinterRow } from "~features/MintPage/live-minting/MintRow";
import {
  getCollectionId,
  mapMinters,
  Minter,
} from "~features/MintPage/live-minting/Minter";

import useMintState from "../../../hooks/useMintState";
import {
  GetSalePageCollectionQuery,
  GetSalePageMintVectorsQuery,
} from "../queries.graphql.generated";

export type MintVector =
  GetSalePageMintVectorsQuery["getMintVectorsByCollection"][0];
export type Collection = GetSalePageCollectionQuery["getPublicCollection"];

export const ROW_HEIGHT = 35.59;
export const SCROLL_HEIGHT = 400;
const MAX_MINTERS = 15;

export const LiveMinting = memo(() => {
  const {
    collectionLoading,
    collection,
    address,
    chainId,
    incrementLiveMintersAdded,
  } = useMintState();
  const theme = useMantineTheme();

  const [animations, setAnimations] = useState({
    addingMinter: false,
    addRowAnimation: false,
    moveRowsAnimation: false,
    highlightRowAnimation: false,
  });

  const [lastMinterAdded, setLastMinterAdded] = useState<Minter | null>(null);
  const [nextMinterToAdd, setNextMinterToAdd] = useState<Minter | null>(null);
  const [mintersAdded, setMintersAdded] = useState<Minter[] | null>(null);
  const [eventsBuffer, setEventsBuffer] = useState<Minter[]>([]);
  const [now, setNow] = useState<number>(Date.now());
  const [paused, setPaused] = useState(false);
  const enableLiveMinting = useFeatureFlags(FEATURE_FLAGS.ENABLE_LIVE_MINTING);

  const { data, loadInitialEvents, isInitialDataLoaded, initialData } =
    useLiveEvents({
      isWebsocketPaused: false,
      maxCount: MAX_MINTERS,
      mainnetOnly: true,
    });

  const minterRows = useMemo(() => mintersAdded || [], [mintersAdded]);

  const collectionId = useMemo(() => {
    if (!collection) return null;
    return getCollectionId(
      address,
      chainId,
      collection.editionId
    ).toLowerCase();
  }, [address, chainId, collection?.editionId]);

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  // fetch initial minters
  useEffect(() => {
    if (!isInitialDataLoaded && enableLiveMinting && collectionId) {
      loadInitialEvents(MAX_MINTERS, collectionId);
    }
  }, [isInitialDataLoaded, loadInitialEvents, enableLiveMinting, collectionId]);

  // Add initial minters
  useEffect(() => {
    if (isInitialDataLoaded && initialData && mintersAdded === null) {
      setNow(Date.now());
      const newItems = mapMinters(initialData, [], collectionId);
      setMintersAdded(newItems);
    }
  }, [isInitialDataLoaded, initialData, mintersAdded, collectionId]);

  // Add new minters to buffer
  useEffect(() => {
    if (data && mintersAdded !== null) {
      setEventsBuffer((prevItems) => {
        const currentMinters = [
          ...prevItems,
          ...mintersAdded,
          ...(lastMinterAdded ? [lastMinterAdded] : []),
          ...(nextMinterToAdd ? [nextMinterToAdd] : []),
        ];

        const newItems = mapMinters(data, currentMinters, collectionId).slice(
          0,
          MAX_MINTERS
        );

        const remainingPrevItems = prevItems.slice(
          0,
          MAX_MINTERS - newItems.length
        );

        if (newItems.length === 0) {
          return prevItems;
        }

        return [...newItems, ...remainingPrevItems];
      });
    }
  }, [data, collectionId, mintersAdded, lastMinterAdded, nextMinterToAdd]);

  // Add new minter to the list
  useEffect(() => {
    if (!animations.addingMinter && eventsBuffer.length && !paused) {
      const nextMinter = eventsBuffer[0];
      if (!nextMinter) return;
      setEventsBuffer((prevItems) => prevItems.slice(1));

      setNextMinterToAdd(nextMinter);
      if (lastMinterAdded) {
        setMintersAdded((prev) => {
          if (prev) return [lastMinterAdded, ...prev.slice(0, MAX_MINTERS - 1)];
          return [lastMinterAdded];
        });
      }
      setAnimations({
        addingMinter: true,
        highlightRowAnimation: false,
        moveRowsAnimation: true,
        addRowAnimation: false,
      });
      setLastMinterAdded(null);
    }
  }, [animations.addingMinter, eventsBuffer, paused, lastMinterAdded]);

  const runAddRowAnimation = useCallback(() => {
    setLastMinterAdded(nextMinterToAdd);
    setNextMinterToAdd(null);

    setAnimations((prev) => ({
      ...prev,
      addRowAnimation: true,
      highlightRowAnimation: true,
      moveRowsAnimation: false,
    }));
    setNow(Date.now());
  }, [nextMinterToAdd]);

  const runHighlightRowAnimation = () =>
    setAnimations((prev) => ({
      ...prev,
      highlightRowAnimation: false,
    }));

  const onMintAdded = useCallback(() => {
    setAnimations((prev) => {
      if (prev.addingMinter) incrementLiveMintersAdded();
      return {
        ...prev,
        addingMinter: false,
      };
    });
  }, [incrementLiveMintersAdded]);

  const onMouseEnterLink = useCallback(() => {
    setPaused(true);
  }, []);

  const onMouseLeaveLink = useCallback(() => {
    setPaused(false);
  }, []);

  if (
    !enableLiveMinting ||
    (!minterRows.length && !eventsBuffer.length) ||
    collectionLoading ||
    mintersAdded === null
  ) {
    return <></>;
  }

  return (
    <Box>
      <Group position="apart">
        <Group align="center" spacing={8} p={0} mb={8}>
          <Text size="xs" fw={WEIGHT_BOLD} color={TEXT_COLOR.SECONDARY}>
            Activity
          </Text>
          <Activity
            height={16}
            width={16}
            color={theme.fn.themeColor(TEXT_COLOR.SECONDARY)}
          />
        </Group>
        {paused && (
          <Text size="xs" color={TEXT_COLOR.PLACEHOLDER}>
            Paused
          </Text>
        )}
      </Group>
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          h="100%"
          sx={{
            position: "relative",
          }}
        >
          <ScrollArea offsetScrollbars h={SCROLL_HEIGHT}>
            <Box h={animations.addRowAnimation ? ROW_HEIGHT : "unset"}>
              <Transition
                mounted={animations.addRowAnimation}
                transition={"fade"}
                duration={300}
                timingFunction="ease-out"
                onEntered={runHighlightRowAnimation}
              >
                {(itemStyles) => (
                  <Box
                    style={{
                      ...itemStyles,
                    }}
                  >
                    {lastMinterAdded && (
                      <MinterRow
                        highlighted={animations.highlightRowAnimation}
                        onAnimationEnd={onMintAdded}
                        {...lastMinterAdded}
                        now={now}
                        onMouseEnterLink={onMouseEnterLink}
                        onMouseLeaveLink={onMouseLeaveLink}
                      />
                    )}
                  </Box>
                )}
              </Transition>
            </Box>

            {minterRows && (
              <Stack
                spacing={0}
                sx={{
                  position: "relative",
                  transform: animations.moveRowsAnimation
                    ? `translateY(${ROW_HEIGHT}px)`
                    : undefined,
                  transition: animations.moveRowsAnimation
                    ? "transform 200ms ease-out"
                    : undefined,
                }}
                onTransitionEnd={runAddRowAnimation}
              >
                {minterRows.map((minter) => (
                  <MinterRow
                    key={minter.id}
                    highlighted={false}
                    {...minter}
                    now={now}
                    onMouseEnterLink={onMouseEnterLink}
                    onMouseLeaveLink={onMouseLeaveLink}
                  />
                ))}
              </Stack>
            )}
          </ScrollArea>
        </Box>
        <Box
          w="100%"
          h="30%"
          sx={(theme) => ({
            position: "absolute",
            bottom: 0,
            left: 0,
            background: theme.fn.linearGradient(
              0,
              theme.fn.themeColor(BACKGROUND_COLOR.PRIMARY),
              "transparent"
            ),
            pointerEvents: "none",
            zIndex: 1,
          })}
        />
      </Box>
    </Box>
  );
});
